import { signInWithCustomToken, signOut } from 'firebase/auth';
import { auth } from '../config/firebase';

const AUTH_KEY = 'zapata_planificador_auth';
const SESSION_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 días

// Canjea un custom token de Firebase por una sesión de Firebase Auth. Silencioso:
// si falla, la app sigue funcionando con la sesión HMAC (solo request.auth
// quedará null en las reglas de Firestore hasta el siguiente intento).
async function signInFirebase(customToken) {
  if (!customToken) return false;
  try {
    await signInWithCustomToken(auth, customToken);
    return true;
  } catch (e) {
    console.warn('[authService] signInWithCustomToken falló:', e?.code || e?.message);
    return false;
  }
}

export const authService = {
  login: async (email, password) => {
    // Sin timeout, un backend lento/colgado dejaba el spinner girando para
    // siempre: nada de error, nada de evento, el usuario se quedaba
    // atascado en la pantalla de login sin saber qué pasaba (Rafa,
    // 2026-11-03). 15s es generoso para un cold start de Netlify Functions.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch('/.netlify/functions/loginUser', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
        signal: controller.signal,
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || 'Credenciales incorrectas' };
      }

      const { sessionToken, firebaseToken, ...user } = data;
      const sessionData = {
        user,
        token: sessionToken || null,
        firebaseToken: firebaseToken || null,
        timestamp: Date.now(),
        expiresAt: Date.now() + SESSION_DURATION,
      };

      localStorage.setItem(AUTH_KEY, JSON.stringify(sessionData));
      await signInFirebase(firebaseToken);
      return { success: true, user };
    } catch (e) {
      if (e?.name === 'AbortError') {
        return { success: false, error: 'El servidor está tardando demasiado en responder. Comprueba tu conexión e inténtalo de nuevo.' };
      }
      return { success: false, error: 'Error de conexión. Comprueba tu red.' };
    } finally {
      clearTimeout(timeoutId);
    }
  },

  getCurrentSession: () => {
    try {
      const sessionData = localStorage.getItem(AUTH_KEY);
      if (!sessionData) return null;

      const parsed = JSON.parse(sessionData);
      if (Date.now() > parsed.expiresAt) {
        authService.logout();
        return null;
      }

      return parsed;
    } catch {
      authService.logout();
      return null;
    }
  },

  isAuthenticated: () => authService.getCurrentSession() !== null,

  logout: () => {
    localStorage.removeItem(AUTH_KEY);
    signOut(auth).catch(() => {});
  },

  getCurrentUser: () => {
    const session = authService.getCurrentSession();
    return session ? session.user : null;
  },

  getToken: () => {
    const session = authService.getCurrentSession();
    return session?.token || null;
  },

  // Cabecera lista para spread en cualquier fetch a una función de Netlify:
  //   fetch(url, { headers: { 'Content-Type': 'application/json', ...authService.authHeader() } })
  // Objeto vacío si no hay sesión — no rompe la petición, simplemente no autentica.
  authHeader: () => {
    const token = authService.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  },

  updateUser: (userData) => {
    const session = authService.getCurrentSession();
    if (!session) return;
    const updated = { ...session, user: { ...session.user, ...userData } };
    localStorage.setItem(AUTH_KEY, JSON.stringify(updated));
  },

  // Garantiza que hay una sesión de Firebase Auth activa. El SDK restaura sola
  // la sesión desde IndexedDB tras un reload; solo si no hay usuario (primer
  // login en el navegador, IndexedDB limpiada, token revocado) pide un custom
  // token fresco a refreshFirebaseAuth y lo canjea. Idempotente y silenciosa.
  ensureFirebaseAuth: async () => {
    try { await auth.authStateReady(); } catch { /* SDK antiguo sin authStateReady */ }
    if (auth.currentUser) return true;

    const session = authService.getCurrentSession();
    if (!session?.token) return false;

    // 1º intento: el token que vino en el login (puede estar caducado tras 1h)
    if (session.firebaseToken && await signInFirebase(session.firebaseToken)) return true;

    // 2º intento: pedir uno fresco
    try {
      const r = await fetch('/.netlify/functions/refreshFirebaseAuth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
      });
      if (!r.ok) return false;
      const d = await r.json();
      if (!d.firebaseToken) return false;
      // Cachea el nuevo para el siguiente arranque
      const updated = { ...session, firebaseToken: d.firebaseToken };
      localStorage.setItem(AUTH_KEY, JSON.stringify(updated));
      return await signInFirebase(d.firebaseToken);
    } catch {
      return false;
    }
  },
};
